import React, { useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { SRHT_EXAM_REGULATION_CONTENT } from './srhtExamRegulationContent';

function SectionList({ items }) {
  return (
    <List dense sx={{ py: 0 }}>
      {items.map((item) => (
        <ListItem key={item} disableGutters sx={{ display: 'list-item', ml: 2 }}>
          <ListItemText primary={item} />
        </ListItem>
      ))}
    </List>
  );
}

function SectionContent({ section }) {
  const beforeGroupsParagraphs = Array.isArray(section.beforeGroupsParagraphs)
    ? section.beforeGroupsParagraphs
    : Array.isArray(section.paragraphs)
      ? section.paragraphs
      : [];
  const afterGroupsParagraphs = Array.isArray(section.afterGroupsParagraphs)
    ? section.afterGroupsParagraphs
    : [];

  return (
    <Stack spacing={2}>
      {beforeGroupsParagraphs.map((paragraph) => (
          <Typography key={paragraph} color="text.secondary">
            {paragraph}
          </Typography>
      ))}

      {Array.isArray(section.groups)
        ? section.groups.map((group) => (
          <Card key={`${group.title || 'group'}-${(group.items || []).join('|')}`} variant="outlined">
            <CardContent>
              {group.title ? (
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  {group.title}
                </Typography>
              ) : null}
              <SectionList items={group.items || []} />
            </CardContent>
          </Card>
        ))
        : null}

      {afterGroupsParagraphs.map((paragraph) => (
        <Typography key={paragraph} color="text.secondary">
          {paragraph}
        </Typography>
      ))}

      {Array.isArray(section.children)
        ? section.children.map((child) => (
          <Accordion key={child.id} disableGutters defaultExpanded={Boolean(child.defaultExpanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {child.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SectionContent section={child} />
            </AccordionDetails>
          </Accordion>
        ))
        : null}
    </Stack>
  );
}

export default function SrhtExamRegulationPanel() {
  const content = SRHT_EXAM_REGULATION_CONTENT;
  const defaultExpandedSections = useMemo(
    () => content.sections.filter((section) => section.defaultExpanded).map((section) => section.id),
    [content.sections],
  );
  const [expandedSections, setExpandedSections] = useState(defaultExpandedSections);

  const handleToggleSection = (sectionId) => (_, expanded) => {
    setExpandedSections((current) => {
      if (expanded) {
        return current.includes(sectionId) ? current : [...current, sectionId];
      }
      return current.filter((item) => item !== sectionId);
    });
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {content.subtitle}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {content.source}
        </Typography>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button
          component="a"
          href={content.referenceDocument.href}
          download={content.referenceDocument.filename}
          variant="outlined"
        >
          {content.referenceDocument.label}
        </Button>
      </Stack>

      {Array.isArray(content.quickFacts) && content.quickFacts.length > 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1.5 }}>
              <WarningAmberIcon color="warning" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Schnellübersicht
              </Typography>
            </Stack>
            <SectionList items={content.quickFacts} />
          </CardContent>
        </Card>
      ) : null}

      <Divider />

      <Stack spacing={1.5}>
        {content.sections.map((section) => (
          <Accordion
            key={section.id}
            expanded={expandedSections.includes(section.id)}
            onChange={handleToggleSection(section.id)}
            disableGutters
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {section.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SectionContent section={section} />
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </Stack>
  );
}
